import React from 'react';
import { formatInTz } from '../../../lib/date-utils';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import Image from 'next/image';
import * as BookingActions from '../../../actions/booking';
import appUtils from '../../../utils/appUtils';
import MomentFormatter from '../../../utils/MomentFormatter';
import { i18n, withTranslation, WithTranslation } from 'next-i18next';
import Main from './Main';
import { X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@lb-classroom/ui';

interface Tutor {
  name: string;
  profile_avatar_url: string;
}

interface GeoData {
  timeZone: string;
}

interface CurrentUser {
  js_time_zone?: string;
}

interface RootState {
  booking: {
    saving: boolean;
  };
  modal: {
    isOpen: boolean;
  };
  geoData: {
    geoData: GeoData;
  };
  auth: {
    user: CurrentUser;
  };
}

interface BookingModalOwnProps {
  closeModal?: boolean;
  hideModal: () => void;
  ticketCount: number;
  duration: number;
  tutorId: string;
  tutor: Tutor;
  startTimestamp: number;
  endTimestamp: number;
}

interface BookingModalStateProps {
  fromModal?: boolean;
  saving?: boolean;
  geoData?: GeoData;
  currentUser?: CurrentUser;
}

interface BookingModalDispatchProps {
  bookingActions: Record<string, (...args: unknown[]) => unknown>;
}

type BookingModalContentProps = BookingModalOwnProps &
  BookingModalStateProps &
  BookingModalDispatchProps &
  WithTranslation;

function mapStateToProps(state: RootState) {
  const { booking, modal, geoData, auth } = state;
  const { saving } = booking;
  return {
    saving,
    fromModal: modal.isOpen,
    geoData: geoData.geoData,
    currentUser: auth.user,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    bookingActions: bindActionCreators(
      BookingActions as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >,
      dispatch,
    ),
  };
}

const BookingModalContent = (props: BookingModalContentProps) => {
  const {
    tutorId,
    bookingActions,
    fromModal = false,
    closeModal = false,
    hideModal,
    ticketCount,
    t,
    geoData,
    saving,
    tutor,
    startTimestamp,
    endTimestamp,
    duration,
    currentUser,
  } = props;

  const getCost = () => {
    let ticketCost;
    let subTicketCost;
    const cost = duration === 0 ? 1 : 2;
    if (ticketCount >= cost) {
      ticketCost = cost;
      subTicketCost = 0;
    } else if (ticketCount === cost - 1) {
      ticketCost = cost - 1;
      subTicketCost = 1;
    } else {
      ticketCost = 0;
      subTicketCost = cost;
    }
    return [ticketCost, subTicketCost];
  };

  const onClick = () => {
    const ticketCost = getCost();
    bookingActions.book(
      tutorId,
      startTimestamp,
      endTimestamp,
      ticketCost[0],
      ticketCost[1],
      fromModal,
      closeModal,
    );
  };
  const currentLocale = appUtils.getLocaleFromLang(i18n.language);
  const timeZone = currentUser?.js_time_zone || geoData.timeZone;
  const momentFormatter = new MomentFormatter(currentLocale, timeZone);
  const dateFormat = momentFormatter.getDateFormatWithYear();
  const timeFormat = momentFormatter.getTimeFormat(startTimestamp);
  const endTimeFormat = momentFormatter.getTimeFormat(endTimestamp);
  const avatarUrl = tutor.profile_avatar_url
    .replace('lingualbox-dev', 'lingualbox')
    .replace('lingualbox-staging-sg', 'lingualbox');
  const formattedStart = formatInTz(
    startTimestamp,
    timeZone,
    timeFormat,
    currentLocale,
  );
  const formattedEnd = formatInTz(
    endTimestamp,
    timeZone,
    endTimeFormat || timeFormat,
    currentLocale,
  );
  const timeSentence = `${formattedStart} - ${formattedEnd}`;
  const dateSentence = formatInTz(
    startTimestamp,
    timeZone,
    dateFormat,
    currentLocale,
  );
  const ticketCost = getCost();
  const regularTicketSentence =
    ticketCost[0] === 0
      ? null
      : t('modals.booking.ticketCost', {
          count: ticketCost[0],
          smart_count: ticketCost[0],
        });
  const subTicketSentence =
    ticketCost[1] === 0
      ? null
      : t('modals.booking.subTicketCost', {
          count: ticketCost[1],
          smart_count: ticketCost[1],
        });
  const ticketSentence = [regularTicketSentence, subTicketSentence]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="max-w-sm mx-auto">
      <Card className="z-50 shadow-xl">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>
            <div className="flex items-center">
              <div className="tutor-image">
                <Image
                  width={36}
                  height={36}
                  alt="tutor-avatar"
                  className="rounded-full"
                  src={avatarUrl}
                />
              </div>
              <div className="text-md ml-2">{tutor.name}</div>
            </div>
          </CardTitle>
          <button type="button" className="modal-close" onClick={hideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <Main
            saving={saving}
            duration={duration}
            handleClick={onClick}
            dateSentence={dateSentence}
            ticketSentence={ticketSentence}
            timeSentence={timeSentence}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(BookingModalContent);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
